import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { extractErrorMessage } from "@/api/client";
import {
  prequalCallApi,
  type BuyerCallAnswers,
  type BuyerPrequalCallStateDto,
} from "@/api/prequalCall";
import type { AutosaveState } from "@/design-system/components/ui";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { prequalKeys } from "@/hooks/prequalification/prequalKeys";

/**
 * Reads and writes for the one call — the interview's prequalification half.
 *
 * <p>The call's prequalification answers live on the interview row and save as
 * a WHOLE OBJECT (`PUT …/prequal-call/answers`), debounced, so a dropped call
 * loses at most the last {@link CALL_ANSWERS_DEBOUNCE_MS} of typing (D8). The PUT
 * returns the whole call state, which IS the fresh state, so it is written with
 * `setQueryData`; the interview read is invalidated beside it because the
 * interview's derived answers (FC2 from the cash figure, EXP1 from the ladders,
 * …) move with every save. Invalidating the whole buyer prefix per keystroke is
 * the refetch storm `useBuyerInterview.ts` documents avoiding mid-call; the two
 * writes that MOVE THE RECORD — Prequalify here, Complete there — invalidate
 * `buyerKeys.all`.</p>
 */

/** Matches the interview drawer's text debounce: a typed figure is one write,
 *  and hanging up mid-figure loses nothing worth having. */

/** A change to the call's answers: the fields to set, or a function of the
 *  latest answers returning them. */
export type CallAnswersPatch =
  | Partial<BuyerCallAnswers>
  | ((latest: BuyerCallAnswers) => Partial<BuyerCallAnswers>);

export const CALL_ANSWERS_DEBOUNCE_MS = 600;

/** The call state. Buyers.Update + ownership on the server, so a reader who
 *  cannot edit never asks (`enabled`). */
export function usePrequalCallState(buyerLeadId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.prequalCall(buyerLeadId),
    queryFn: () => prequalCallApi.getState(buyerLeadId),
    enabled,
  });
}

interface SaveVars {
  seq: number;
  answers: BuyerCallAnswers;
}

export function useSavePrequalCallAnswers(
  buyerLeadId: string,
  applyIfLatest: (seq: number) => boolean,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ answers }: SaveVars) => prequalCallApi.saveAnswers(buyerLeadId, answers),
    onSuccess: (state: BuyerPrequalCallStateDto, vars) => {
      // Only the newest in-flight save may install its state, or a slow early
      // PUT lands after a fast later one and Prequalify's gate goes backwards.
      if (applyIfLatest(vars.seq)) {
        qc.setQueryData(buyerKeys.prequalCall(buyerLeadId), state);
      }
      // The interview's derived answers and `derivedQuestionIds` follow the
      // call's answers, so the live drawer has to re-read them.
      void qc.invalidateQueries({ queryKey: buyerKeys.interview(buyerLeadId) });
    },
  });
}

/** Run the prequalification. Generates documents; emails nothing. */
export function usePrequalifyCall(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { confirmDuplicate?: boolean }) =>
      prequalCallApi.prequalify(buyerLeadId, body),
    onSuccess: () => {
      // A submission, its documents, the buyer's prequal link and the call
      // state's newest prequal all moved — and a new submission is on the
      // admin prequalification list and detail too.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: prequalKeys.all });
    },
  });
}

/**
 * The call answers as the drawer edits them: local state seeded once per open,
 * every change debounced into one whole-object save.
 *
 * <p>Local answers are the input and the server state is the output; they never
 * flow back (the same rule the interview drawer seeds by), so a save response
 * landing mid-typing cannot overwrite a figure typed since.</p>
 */
export function useCallAnswersAutosave(buyerLeadId: string, open: boolean) {
  const stateQuery = usePrequalCallState(buyerLeadId, open);
  const state = stateQuery.data;

  const [answers, setAnswers] = useState<BuyerCallAnswers | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [saveState, setSaveState] = useState<AutosaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Seeded during render, once per open — see BuyerInterviewDrawer.
  if (open && state && !seeded) {
    setSeeded(true);
    setAnswers(state.answers ?? {});
  }
  if (!open && seeded) {
    setSeeded(false);
    setAnswers(null);
  }

  const answersRef = useRef<BuyerCallAnswers | null>(null);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const seqRef = useRef(0);
  const latestSeqRef = useRef(0);
  const inflightRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);

  const applyIfLatest = useCallback((seq: number) => seq === latestSeqRef.current, []);
  const save = useSavePrequalCallAnswers(buyerLeadId, applyIfLatest);
  const saveAsync = save.mutateAsync;

  /** Send the current answers now. Resolves when that save settles; a failure
   *  is reported through the indicator and rethrown to the awaiting caller. */
  const fire = useCallback(async () => {
    const current = answersRef.current;
    pendingRef.current = false;
    if (!current) return;
    const seq = ++seqRef.current;
    latestSeqRef.current = seq;
    inflightRef.current += 1;
    setSaveState("saving");
    try {
      await saveAsync({ seq, answers: current });
      inflightRef.current -= 1;
      setFailed(false);
      setLastSavedAt(new Date().toISOString());
      if (inflightRef.current === 0) setSaveState("saved");
    } catch (error) {
      inflightRef.current -= 1;
      // Still pending, so Retry and the next flush re-send it.
      pendingRef.current = true;
      setFailed(true);
      setSaveState("error");
      throw new Error(extractErrorMessage(error));
    }
  }, [saveAsync]);

  const schedule = useCallback(() => {
    pendingRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void fire().catch(() => undefined);
    }, CALL_ANSWERS_DEBOUNCE_MS);
  }, [fire]);

  /** Merge a change into the answers and queue the save. A FUNCTION patch is
   *  computed from the LATEST answers, not the render's: a control that writes
   *  several keys in one event (a restructured knockout writes its primary, its
   *  follow-up and its resolved key in turn) would otherwise have each write
   *  spread a stale snapshot and erase the one before it. */
  const update = useCallback(
    (patch: CallAnswersPatch) => {
      const current = answersRef.current ?? {};
      const delta = typeof patch === "function" ? patch(current) : patch;
      const next = { ...current, ...delta };
      answersRef.current = next;
      setAnswers(next);
      schedule();
    },
    [schedule],
  );

  /** Write whatever is still waiting on the debounce, now. Prequalify awaits
   *  it, so the server prequalifies exactly what is on screen. */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current) await fire();
  }, [fire]);

  // A timer outliving the drawer would write from a screen nobody can see.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return {
    stateQuery,
    state,
    answers,
    update,
    flush,
    retry: () => void fire().catch(() => undefined),
    saveState,
    lastSavedAt,
    failed,
  };
}

export type CallAnswersAutosave = ReturnType<typeof useCallAnswersAutosave>;
